/**
 * Pachisi Move History Component
 *
 * Scrollable panel showing move history in chronological order.
 */

import React, { useEffect, useRef } from 'react';
import { MoveRecord, PlayerColor, GRACE_THROWS } from '../types';
import './MoveHistory.css';

interface MoveHistoryProps {
  moves: MoveRecord[];
  maxHeight?: number;
}

const COLOR_EMOJI: Record<PlayerColor, string> = {
  red: '🔴',
  blue: '🔵',
  green: '🟢',
  yellow: '🟡',
};

function formatMove(move: MoveRecord): string {
  const emoji = COLOR_EMOJI[move.player];
  const isGrace = GRACE_THROWS.includes(move.shellsUp);
  let desc = `${emoji} ${move.player} threw ${move.shellsUp} shells (${move.moveValue})`;

  if (move.action === 'enter') {
    desc += ` — Pawn ${move.pawnIndex + 1} entered the board`;
  } else if (move.action === 'move') {
    if (move.toPosition.location === 'home') {
      desc += ` — Pawn ${move.pawnIndex + 1} reached home! 🏠`;
    } else if (move.toPosition.location === 'home_column') {
      desc += ` — Pawn ${move.pawnIndex + 1} entered home column`;
    } else {
      desc += ` — Pawn ${move.pawnIndex + 1} moved`;
    }
  } else if (move.action === 'skip') {
    desc += ' — No valid moves, turn skipped';
  }

  if (move.captured) {
    desc += ` 💥 Captured ${move.captured.player} pawn ${move.captured.pawnIndex + 1}!`;
  }

  if (move.extraTurn) {
    desc += ' 🐚 Grace — extra turn!';
  }

  return desc;
}

const MoveHistory: React.FC<MoveHistoryProps> = ({ moves, maxHeight = 200 }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest move
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [moves.length]);

  if (moves.length === 0) {
    return (
      <div className="pachisi-history">
        <h3 className="pachisi-history__title">Move History</h3>
        <p className="pachisi-history__empty">No moves yet. Throw the cowries to start!</p>
      </div>
    );
  }

  return (
    <div className="pachisi-history">
      <h3 className="pachisi-history__title">Move History</h3>
      <div
        className="pachisi-history__list"
        ref={scrollRef}
        style={{ maxHeight }}
        role="log"
        aria-label="Move history"
        aria-live="polite"
      >
        {moves.map((move) => (
          <div
            key={move.moveNumber}
            className={`pachisi-history__entry pachisi-history__entry--${move.player}`}
          >
            <span className="pachisi-history__number">{move.moveNumber}.</span>
            <span className="pachisi-history__text">{formatMove(move)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { MoveHistory };
export default MoveHistory;
